import { useMutation, useQuery } from '@tanstack/react-query';
import moment from 'moment';

import type { DriverSpeedingTableRow, PageType, SpeedingSummary, VehicleSpeedingTableRow } from '@cobliteam/common-types';

import {
  AnalyticsSpeedingRequester,
  GrahpQLSpeedRequester,
  type HeatmapChart,
  type HomeSpeed,
  type ReportAggregatedBy,
  type SpeedingChartRequest,
  type SpeedingReportRequest,
  type SpeedingRequest,
  type TimeseriesChart,
} from '@cobliteam/api-lib';

import { Environment } from '@cobliteam/react-environments';

const speedGraphQLRequester = new GrahpQLSpeedRequester();
const env = Environment.getInstance().getEnvironment();
const analyticsSpeedingRequester = new AnalyticsSpeedingRequester({
  host: env.apiHost,
});

const QUERY_KEY = 'speeding';
const TIMESERIES_CHART_KEY = 'speeding-timeseries-chart';
const HEATMAP_KEY = 'speeding-heatmap';

const useSpeed = () => {
  const getHomeSpeedSummary = () => {
    const previousDayStartInMillis = moment().subtract(1, 'day').startOf('day').valueOf();
    const previousDayEndInMillis = moment().subtract(1, 'day').endOf('day').valueOf();

    const intervalInDays = 1;

    return useQuery<HomeSpeed>({
      queryKey: [`${QUERY_KEY}-home-summary`],
      queryFn: () => speedGraphQLRequester.getHomeSpeedSummary(previousDayStartInMillis, previousDayEndInMillis, intervalInDays),
      refetchOnMount: false,
    });
  };

  const useGetSpeedingSummary = (speedingRequest: SpeedingRequest, aggregatedBy: PageType) =>
    useQuery<SpeedingSummary>({
      queryKey: [`${QUERY_KEY}_SUMMARY`, JSON.stringify(speedingRequest), aggregatedBy],
      queryFn: () => analyticsSpeedingRequester.getSpeedingSummary(speedingRequest, aggregatedBy),
      staleTime: 300000,
    });

  const useGetSpeeding = (speedingRequest: SpeedingRequest, aggregatedBy: PageType) =>
    useQuery<VehicleSpeedingTableRow[] | DriverSpeedingTableRow[]>({
      queryKey: [`${QUERY_KEY}_TABLE`, JSON.stringify(speedingRequest), aggregatedBy],
      queryFn: () => analyticsSpeedingRequester.getSpeeding(speedingRequest, aggregatedBy),
      staleTime: 300000,
    });

  const useRequestSpeedingReport = useMutation<
    string,
    unknown,
    {
      reportRequest: SpeedingReportRequest;
      aggregatedBy: ReportAggregatedBy;
    }
  >({
    mutationFn: (variables) => analyticsSpeedingRequester.requestSpeedingReport(variables.reportRequest, variables.aggregatedBy),
  });

  const useGetSpeedingTimeseriesChart = (chartRequest: SpeedingChartRequest) =>
    useQuery<TimeseriesChart[]>({
      queryKey: [TIMESERIES_CHART_KEY, JSON.stringify(chartRequest)],
      queryFn: () => analyticsSpeedingRequester.getTimeseriesChart(chartRequest),
      staleTime: 300000,
    });

  const useGetSpeedingHeatmapChart = (chartRequest: SpeedingChartRequest) =>
    useQuery<HeatmapChart[]>({
      queryKey: [HEATMAP_KEY, JSON.stringify(chartRequest)],
      queryFn: () => analyticsSpeedingRequester.getHeatmapChart(chartRequest),
      staleTime: 300000,
    });

  return {
    getHomeSpeedSummary,
    getSpeedingSummary: useGetSpeedingSummary,
    getSpeedingRows: useGetSpeeding,
    getSpeedingTimeseriesChart: useGetSpeedingTimeseriesChart,
    getSpeedingHeatmapChart: useGetSpeedingHeatmapChart,
    requestSpeedingReport: useRequestSpeedingReport,
  };
};

export default useSpeed;
